/**
 * Rapid X Offline Database Service
 * Uses Dexie.js (IndexedDB wrapper) for offline data storage
 */
import Dexie from 'dexie';

// Create database instance
const db = new Dexie('RapidXOffline');

// Define schema
db.version(1).stores({
  // Patrol data
  patrols: 'id, status, started_at, officer_id, site_id, synced',
  checkpoints: '++id, patrol_id, checkpoint_id, scanned_at, synced',
  gpsCoordinates: '++id, patrol_id, timestamp, synced',
  
  // Pending actions (to sync when online)
  pendingStops: '++id, patrol_id, timestamp, synced',
  pendingCheckpoints: '++id, patrol_id, checkpoint_id, timestamp, synced',
  pendingIncidents: '++id, timestamp, synced',
  
  // Photo references
  photoRefs: '++id, entity_type, entity_id, blob_key, synced',
  
  // Cache
  routeCache: 'id, site_id, updated_at',
  siteCache: 'id, updated_at',
  
  // Sync metadata
  syncMeta: 'key',
});

// =============================================================================
// PATROL OPERATIONS
// =============================================================================

export const offlinePatrols = {
  /**
   * Save a patrol locally
   */
  async save(patrol) {
    return db.patrols.put({
      ...patrol,
      synced: false,
      local_updated_at: new Date().toISOString(),
    });
  },

  /**
   * Get patrol by ID
   */
  async get(patrolId) {
    return db.patrols.get(patrolId);
  },

  /**
   * Get all active patrols
   */
  async getActive() {
    return db.patrols.where('status').anyOf(['active', 'in_progress', 'paused']).toArray();
  },

  /**
   * Get all unsynced patrols
   */
  async getUnsynced() {
    return db.patrols.where('synced').equals(0).toArray();
  },

  /**
   * Mark patrol as synced
   */
  async markSynced(patrolId, serverTimestamp) {
    return db.patrols.update(patrolId, {
      synced: true,
      server_synced_at: serverTimestamp,
    });
  },

  /**
   * Update patrol status locally
   */
  async updateStatus(patrolId, status, additionalData = {}) {
    return db.patrols.update(patrolId, {
      status,
      ...additionalData,
      synced: false,
      local_updated_at: new Date().toISOString(),
    });
  },
};

// =============================================================================
// CHECKPOINT OPERATIONS
// =============================================================================

export const offlineCheckpoints = {
  /**
   * Save a checkpoint scan
   */
  async save(checkpointScan) {
    return db.checkpoints.add({
      ...checkpointScan,
      synced: false,
      scanned_at: checkpointScan.scanned_at || new Date().toISOString(),
    });
  },

  /**
   * Get checkpoints for a patrol
   */
  async getByPatrol(patrolId) {
    return db.checkpoints.where('patrol_id').equals(patrolId).toArray();
  },

  /**
   * Get all unsynced checkpoints
   */
  async getUnsynced() {
    return db.checkpoints.where('synced').equals(0).toArray();
  },

  /**
   * Mark checkpoint as synced
   */
  async markSynced(id) {
    return db.checkpoints.update(id, { synced: true });
  },
};

// =============================================================================
// GPS TRACKING
// =============================================================================

export const offlineGPS = {
  /**
   * Save GPS coordinate
   */
  async save(patrolId, latitude, longitude) {
    return db.gpsCoordinates.add({
      patrol_id: patrolId,
      latitude,
      longitude,
      timestamp: new Date().toISOString(),
      synced: false,
    });
  },

  /**
   * Get GPS trail for a patrol
   */
  async getByPatrol(patrolId) {
    return db.gpsCoordinates.where('patrol_id').equals(patrolId).toArray();
  },

  /**
   * Clear old GPS data (older than 7 days)
   */
  async clearOld() {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    
    return db.gpsCoordinates
      .where('timestamp')
      .below(cutoff.toISOString())
      .delete();
  },
};

// =============================================================================
// PENDING ACTIONS (OFFLINE QUEUE)
// =============================================================================

export const pendingActions = {
  /**
   * Queue a patrol stop for sync
   */
  async queueStop(stopData) {
    return db.pendingStops.add({
      ...stopData,
      timestamp: new Date().toISOString(),
      synced: false,
    });
  },

  /**
   * Get all pending stops
   */
  async getPendingStops() {
    return db.pendingStops.where('synced').equals(0).toArray();
  },

  /**
   * Clear synced stops
   */
  async clearSyncedStops() {
    return db.pendingStops.where('synced').equals(1).delete();
  },

  /**
   * Mark stop as synced
   */
  async markStopSynced(id) {
    return db.pendingStops.update(id, { synced: true });
  },

  /**
   * Queue a checkpoint scan for sync
   */
  async queueCheckpoint(checkpointData) {
    return db.pendingCheckpoints.add({
      ...checkpointData,
      timestamp: new Date().toISOString(),
      synced: false,
    });
  },

  /**
   * Get all pending checkpoints
   */
  async getPendingCheckpoints() {
    return db.pendingCheckpoints.where('synced').equals(0).toArray();
  },

  /**
   * Mark checkpoint as synced
   */
  async markCheckpointSynced(id) {
    return db.pendingCheckpoints.update(id, { synced: true });
  },

  /**
   * Queue an incident for sync
   */
  async queueIncident(incidentData) {
    return db.pendingIncidents.add({
      ...incidentData,
      offline_id: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      synced: false,
    });
  },

  /**
   * Get all pending incidents
   */
  async getPendingIncidents() {
    return db.pendingIncidents.where('synced').equals(0).toArray();
  },

  /**
   * Mark incident as synced
   */
  async markIncidentSynced(id, serverId) {
    return db.pendingIncidents.update(id, { synced: true, server_id: serverId });
  },

  /**
   * Get total pending actions count
   */
  async getPendingCount() {
    const stops = await db.pendingStops.where('synced').equals(0).count();
    const checkpoints = await db.pendingCheckpoints.where('synced').equals(0).count();
    const incidents = await db.pendingIncidents.where('synced').equals(0).count();
    return stops + checkpoints + incidents;
  },
};

// =============================================================================
// PHOTO STORAGE
// =============================================================================

export const offlinePhotos = {
  /**
   * Save photo reference (blob stored in IndexedDB)
   */
  async save(entityType, entityId, blobKey, metadata = {}) {
    return db.photoRefs.add({
      entity_type: entityType,
      entity_id: entityId,
      blob_key: blobKey,
      metadata,
      timestamp: new Date().toISOString(),
      synced: false,
    });
  },

  /**
   * Get photos for an entity
   */
  async getByEntity(entityType, entityId) {
    return db.photoRefs
      .where(['entity_type', 'entity_id'])
      .equals([entityType, entityId])
      .toArray();
  },

  /**
   * Get all unsynced photos
   */
  async getUnsynced() {
    return db.photoRefs.where('synced').equals(0).toArray();
  },
};

// =============================================================================
// CACHE OPERATIONS
// =============================================================================

export const cache = {
  /**
   * Cache patrol routes
   */
  async cacheRoutes(routes) {
    const now = new Date().toISOString();
    const routesWithTimestamp = routes.map(r => ({
      ...r,
      updated_at: now,
    }));
    return db.routeCache.bulkPut(routesWithTimestamp);
  },

  /**
   * Get cached routes
   */
  async getRoutes() {
    return db.routeCache.toArray();
  },

  /**
   * Cache sites
   */
  async cacheSites(sites) {
    const now = new Date().toISOString();
    const sitesWithTimestamp = sites.map(s => ({
      ...s,
      updated_at: now,
    }));
    return db.siteCache.bulkPut(sitesWithTimestamp);
  },

  /**
   * Get cached sites
   */
  async getSites() {
    return db.siteCache.toArray();
  },

  /**
   * Clear all cache
   */
  async clearAll() {
    await db.routeCache.clear();
    await db.siteCache.clear();
  },
};

// =============================================================================
// SYNC METADATA
// =============================================================================

export const syncMeta = {
  /**
   * Set last sync time
   */
  async setLastSync(key, timestamp) {
    return db.syncMeta.put({ key, timestamp, synced_at: new Date().toISOString() });
  },

  /**
   * Get last sync time
   */
  async getLastSync(key) {
    const meta = await db.syncMeta.get(key);
    return meta?.timestamp;
  },
};

// =============================================================================
// SYNC SERVICE
// =============================================================================

export const syncService = {
  /**
   * Check if we're online
   */
  isOnline() {
    return navigator.onLine;
  },

  /**
   * Sync all pending actions
   */
  async syncAll(api) {
    if (!this.isOnline()) {
      console.log('[Offline] Cannot sync - no network');
      return { success: false, reason: 'offline' };
    }

    const results = {
      stops: { synced: 0, failed: 0 },
      checkpoints: { synced: 0, failed: 0 },
      incidents: { synced: 0, failed: 0 },
    };

    try {
      // Sync pending checkpoint scans
      const pendingCheckpoints = await pendingActions.getPendingCheckpoints();
      for (const cp of pendingCheckpoints) {
        try {
          await api.post(`/patrols/runs/${cp.patrol_id}/checkpoints/${cp.checkpoint_id}/scan`, {
            result: cp.result || 'SUCCESS',
            notes: cp.notes || '',
            latitude: cp.latitude,
            longitude: cp.longitude,
            scanned_at: cp.timestamp,
            offline_sync: true,
          });
          await pendingActions.markCheckpointSynced(cp.id);
          results.checkpoints.synced++;
        } catch (err) {
          console.error('[Offline] Checkpoint sync failed:', err);
          results.checkpoints.failed++;
        }
      }

      // Sync pending incidents
      const pendingIncidents = await pendingActions.getPendingIncidents();
      for (const incident of pendingIncidents) {
        try {
          const response = await api.post('/incidents', {
            ...incident,
            pending_sync: true,
            offline_id: incident.offline_id,
          });
          await pendingActions.markIncidentSynced(incident.id, response.data?.id);
          results.incidents.synced++;
        } catch (err) {
          console.error('[Offline] Incident sync failed:', err);
          results.incidents.failed++;
        }
      }

      // Sync pending stops (patrol completions)
      const pendingStops = await pendingActions.getPendingStops();
      for (const stop of pendingStops) {
        try {
          await api.post(`/patrols/${stop.patrol_id}/stop`, {
            stop_reason: stop.stop_reason || 'COMPLETED',
            notes: stop.notes || '',
            final_gps: stop.final_gps,
            stopped_at: stop.timestamp,
            offline_sync: true,
          });
          await pendingActions.markStopSynced(stop.id);
          results.stops.synced++;
        } catch (err) {
          console.error('[Offline] Stop sync failed:', err);
          results.stops.failed++;
        }
      }

      // Update last sync time
      await syncMeta.setLastSync('all', new Date().toISOString());

      const totalSynced = results.stops.synced + results.checkpoints.synced + results.incidents.synced;
      const totalFailed = results.stops.failed + results.checkpoints.failed + results.incidents.failed;

      console.log(`[Offline] Sync complete: ${totalSynced} synced, ${totalFailed} failed`);

      return { success: true, results };
    } catch (error) {
      console.error('[Offline] Sync failed:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Sync just checkpoints
   */
  async syncCheckpoints(api) {
    if (!this.isOnline()) return { success: false, reason: 'offline' };

    const pendingCheckpoints = await pendingActions.getPendingCheckpoints();
    let synced = 0, failed = 0;

    for (const cp of pendingCheckpoints) {
      try {
        await api.post(`/patrols/runs/${cp.patrol_id}/checkpoints/${cp.checkpoint_id}/scan`, {
          result: cp.result || 'SUCCESS',
          notes: cp.notes || '',
          latitude: cp.latitude,
          longitude: cp.longitude,
          scanned_at: cp.timestamp,
          offline_sync: true,
        });
        await pendingActions.markCheckpointSynced(cp.id);
        synced++;
      } catch (err) {
        failed++;
      }
    }

    return { success: true, synced, failed };
  },

  /**
   * Register background sync
   */
  async registerBackgroundSync(tag = 'sync-patrol-data') {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register(tag);
        console.log('[Offline] Background sync registered:', tag);
      } catch (error) {
        console.error('[Offline] Background sync registration failed:', error);
      }
    }
  },
};

// Export database instance for direct access if needed
export default db;
